import React, { useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import InspectionEventModal from '@/pages/Personnel/components/InspectionDashboardComponents/InspectionEventModal';
import InspectionResponseModal from './InspectionResponseModal';
import { gql } from '@apollo/client';

const GET_USER_INSPECTIONS = gql`
   query GetUserInspections {
      getUserInspections {
         id
         applicationNumber
         applicationType
         scheduledDate
         scheduledTime
         location
         inspectionStatus
         preferredSchedule {
            date
            time
         }
      }
   }
`;

const InspectionCalendarView = ({ inspections }) => {
   const [selectedEvent, setSelectedEvent] = useState(null);
   const [showResponseModal, setShowResponseModal] = useState(false);
   const [selectedInspection, setSelectedInspection] = useState(null);

   const events = useMemo(() => {
      return inspections
         .filter(inspection => inspection.inspectionStatus !== 'Completed')
         .map(inspection => {
            const dateObj = new Date(inspection.scheduledDate);
            const eventDate = dateObj.toISOString().split('T')[0];
            const eventTime = inspection.scheduledTime.padStart(5, '0');

            // Color coding based on status
            const getEventColor = (status) => {
               switch (status) {
                  case 'Accepted':
                     return '#16a34a'; // green-600
                  case 'Pending':
                     return '#2563eb'; // blue-600
                  case 'Reschedule Requested':
                     return '#ea580c'; // orange-600
                  case 'New Schedule Proposed':
                     return '#9333ea'; // purple-600
                  default:
                     return '#6b7280'; // gray-500
               }
            };

            return {
               id: inspection.id,
               title: `${inspection.applicationNumber}`,
               start: `${eventDate}T${eventTime}:00`,
               end: `${eventDate}T${eventTime}:00`,
               backgroundColor: getEventColor(inspection.inspectionStatus),
               extendedProps: inspection
            };
         });
   }, [inspections]);

   const handleEventClick = (clickInfo) => {
      const inspection = clickInfo.event.extendedProps;
      setSelectedEvent({
         ...clickInfo.event,
         start: clickInfo.event.start,
         extendedProps: inspection
      });
      setSelectedInspection(inspection);
   };

   const handleResponseClick = () => {
      setShowResponseModal(true);
      setSelectedEvent(null);
   };

   return (
      <div className="h-[700px] bg-background rounded-lg shadow p-4">
         <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
               left: 'prev,next today',
               center: 'title',
               right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            events={events}
            eventClick={handleEventClick}
            slotMinTime="06:00:00"
            slotMaxTime="18:00:00"
            allDaySlot={false}
            height="100%"
            eventContent={(eventInfo) => (
               <div className="p-1">
                  <div className="font-medium text-xs">
                     {eventInfo.event.title}
                  </div>
                  <div className="text-xs opacity-75">
                     {eventInfo.event.extendedProps.applicationType}
                  </div>
                  <div className="text-xs opacity-75">
                     {eventInfo.event.extendedProps.inspectionStatus}
                  </div>
               </div>
            )}
         />

         <InspectionEventModal
            isOpen={!!selectedEvent}
            onClose={() => {
               setSelectedEvent(null);
               setSelectedInspection(null);
            }}
            event={selectedEvent}
            onResponseClick={handleResponseClick}
            showResponseButton={true}
         />

         <InspectionResponseModal
            isOpen={showResponseModal}
            onClose={() => {
               setShowResponseModal(false);
               setSelectedInspection(null);
            }}
            inspection={selectedInspection}
         />
      </div>
   );
};

export default InspectionCalendarView;
